import { Box, Text } from "@radix-ui/themes";
import type {
  ListAlbumDto,
  ListTrackDto,
  ListCreatorDto,
} from "@/lib/lutely-api/gen/dtos";

interface SearchDropdownProps {
  show: boolean;
  albums: ListAlbumDto[];
  songs: ListTrackDto[];
  creators: ListCreatorDto[];
  hasResults: boolean;
  isLoading: boolean;
  /* The whole creator, not just the id: the branded /@slug URL is the
     primary destination and only the caller can build it (LUT-927). */
  onSelectCreator: (creator: ListCreatorDto) => void;
  onSelectAlbum: (albumId: string) => void;
  onSelectSong: (trackId: string) => void;
  className?: string;
}

export default function SearchDropdown({
  show,
  albums,
  songs,
  creators,
  hasResults,
  isLoading,
  onSelectCreator,
  onSelectAlbum,
  onSelectSong,
  className = "",
}: SearchDropdownProps) {
  if (!show) return null;

  return (
    <Box
      className={`absolute top-[calc(100%+8px)] left-0 right-0 z-50 rounded-xl border border-black/10 bg-white shadow-lg overflow-hidden ${className}`}
    >
      <Box className="max-h-[360px] overflow-y-auto p-2">
        {isLoading && (
          <Text className="px-3 py-2 text-[13px] text-[#525966]">
            Searching...
          </Text>
        )}

        {!isLoading && !hasResults && (
          <Text className="px-3 py-2 text-[13px] text-[#525966]">
            No results
          </Text>
        )}

        {!isLoading && creators.length > 0 && (
          <>
            <Text className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#525966]">
              Creators
            </Text>
            {creators.map((creator) => (
              <button
                key={`creator-${creator.creatorId}`}
                type="button"
                onClick={() => onSelectCreator(creator)}
                className="w-full rounded-lg px-3 py-2 text-left hover:bg-[#f3f4f6]"
              >
                <Text className="text-[14px] font-semibold text-blue-900">
                  {creator.stageName}
                </Text>
              </button>
            ))}
          </>
        )}

        {!isLoading && albums.length > 0 && (
          <>
            <Text className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#525966]">
              Albums
            </Text>
            {albums.map((album) => (
              <button
                key={`album-${album.albumId}`}
                type="button"
                onClick={() => onSelectAlbum(album.albumId)}
                className="w-full rounded-lg px-3 py-2 text-left hover:bg-[#f3f4f6]"
              >
                <Box>
                  <Text
                    as="div"
                    className="text-[14px] font-semibold text-blue-900"
                  >
                    {album.title}
                  </Text>
                  <Text as="div" className="text-[12px] text-[#525966]">
                    {album.creator.stageName}
                  </Text>
                </Box>
              </button>
            ))}
          </>
        )}

        {!isLoading && songs.length > 0 && (
          <>
            <Text className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#525966]">
              Songs
            </Text>
            {songs.map((song) => (
              <button
                key={`song-${song.trackId}`}
                type="button"
                onClick={() => onSelectSong(song.trackId)}
                className="w-full rounded-lg px-3 py-2 text-left hover:bg-[#f3f4f6]"
              >
                <Box>
                  <Text
                    as="div"
                    className="text-[14px] font-semibold text-blue-900"
                  >
                    {song.title}
                  </Text>
                  <Text as="div" className="text-[12px] text-[#525966]">
                    {song.creator.stageName}
                  </Text>
                </Box>
              </button>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}
